/**
 * HTTP client for the Express BFF backend.
 * Token is injected after MSAL login via setToken().
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;


class ApiClient {
  private token = '';
  private tokenProvider: (() => Promise<string>) | null = null;
  private partnerToken = '';
  private pricingToken = '';

  setToken(token: string): void { this.token = token; }
  setPartnerToken(token: string): void { this.partnerToken = token; }
  setPricingToken(token: string): void { this.pricingToken = token; }

  /** Called before every request so the token is always fresh (and available before the first call). */
  setTokenProvider(provider: (() => Promise<string>) | null): void { this.tokenProvider = provider; }

  isConfigured(): boolean { return Boolean(BASE_URL); }

  private async headers(): Promise<HeadersInit> {
    if (this.tokenProvider) {
      try {
        const fresh = await this.tokenProvider();
        if (fresh) this.token = fresh;
      } catch { /* keep the last known token */ }
    }
    return {
      'Content-Type': 'application/json',
      ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      ...(this.partnerToken ? { 'X-Partner-Token': this.partnerToken } : {}),
      ...(this.pricingToken ? { 'X-Pricing-Token': this.pricingToken } : {}),
    };
  }

  async get<T>(path: string): Promise<T> {
    const res = await fetch(`${BASE_URL}${path}`, { headers: await this.headers() });
    if (!res.ok) {
      const body = await res.text();
      throw new Error(`API ${path} \u2192 ${res.status}: ${body}`);
    }
    return res.json() as Promise<T>;
  }

  async post<T>(path: string, body: unknown): Promise<T> {
    const res = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: await this.headers(),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`API POST ${path} \u2192 ${res.status}: ${text}`);
    }
    return res.json() as Promise<T>;
  }

  /** POST with additional one-off headers merged in (e.g. a caller-supplied bearer token for a downstream service). */
  async postWithHeaders<T>(path: string, body: unknown, extraHeaders: Record<string, string>): Promise<T> {
    const res = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: { ...(await this.headers()), ...extraHeaders },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`API POST ${path} \u2192 ${res.status}: ${text}`);
    }
    return res.json() as Promise<T>;
  }

  async put<T>(path: string, body: unknown): Promise<T> {
    const res = await fetch(`${BASE_URL}${path}`, {
      method: 'PUT',
      headers: await this.headers(),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`API PUT ${path} \u2192 ${res.status}: ${text}`);
    }
    return res.json() as Promise<T>;
  }

  async delete(path: string): Promise<void> {
    const res = await fetch(`${BASE_URL}${path}`, {
      method: 'DELETE',
      headers: await this.headers(),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`API DELETE ${path} \u2192 ${res.status}: ${text}`);
    }
  }
}

export const apiClient = new ApiClient();